import "./style.scss";
import * as _ from "lodash";
import * as React from "react";

import Section from "../../../../core/Section";
import Row from "../../../../core/Row";
import { Modal } from "../../../../core/Modal";
import Table from "../../../../core/Table";
import { Checkbox } from "../../../../form";
import Draggable from "../../../../core/Draggable";
import Dropable from "../../../../core/Dropable";
import Button from "../../../../core/Button";
import MetricPopover from "./MetricPopover";
import DimensionPopover from "./DimensionPopover";
import PreviewPopover from "./PreviewPopover";

import { Form, Icon, Popover } from "antd";
const FormItem = Form.Item;

interface ReportStructureState {
  dimensionModal: boolean;
  metricModal: boolean;
  [key: string]: boolean;
}

class ReportStructure extends React.Component<
  ReportStructureProps & ReportStructureActions,
  ReportStructureState
> {
  // Modal state
  state: ReportStructureState = {
    dimensionModal: false,
    metricModal: false
  };

  toggle = (key: string) => {
    this.setState(prevState => ({ [key]: !prevState[key] }));
  };

  getPreviewColumns() {
    const columns: any[] = [];

    this.props.dimensions.selected.forEach((dimension: ReportColumn) =>
      columns.push({ key: dimension.name, title: dimension.name })
    );
    this.props.metrics.selected.forEach((metric: ReportColumn) =>
      columns.push({ key: metric.name, title: metric.name })
    );

    return columns;
  }

  // React DND requirements
  handleMetricFind(id: number) {
    const item = this.props.metrics.selected.filter((metric: ReportColumn) => {
      return metric.id === id;
    })[0];
    const index = this.props.metrics.selected.indexOf(item);
    return { item, index };
  }

  handleDimensionFind(id: number) {
    const item = this.props.dimensions.selected.filter(
      (metric: ReportColumn) => {
        return metric.id === id;
      }
    )[0];
    const index = this.props.dimensions.selected.indexOf(item);
    return { item, index };
  }

  // Callbacks
  handleMetricChange(metric: ReportColumn, event: any) {
    this.updateSelectedMetrics(metric, event.target.checked);
    this.props.form.validateFields(["metrics"]);
  }

  updateSelectedMetrics(metric: ReportColumn, checked: boolean) {
    let form = this.props.form;
    if (checked) {
      this.props.addMetric(metric);
      form.setFieldsValue({
        metrics: [...form.getFieldValue("metrics"), metric]
      });
    } else {
      this.props.removeMetric(metric);
      let filteredMetrics = form
        .getFieldValue("metrics")
        .filter((m: ReportColumn) => {
          return m.name !== metric.name;
        });
      form.setFieldsValue({ metrics: filteredMetrics });
    }
  }

  handleMetricReorder(fromIndex: number, toIndex: number) {
    this.props.reorderMetric(fromIndex, toIndex);
  }

  handleDimensionChange(dimension: ReportColumn, event: any) {
    this.updateSelectedDimensions(dimension, event.target.checked);
    this.props.form.validateFields(["dimensions"]);
  }

  updateSelectedDimensions(dimension: ReportColumn, checked: boolean) {
    let form = this.props.form;
    if (checked) {
      this.props.addDimension(dimension);
      form.setFieldsValue({
        dimensions: [...form.getFieldValue("dimensions"), dimension]
      });
    } else {
      this.props.removeDimension(dimension);
      let filteredDimensions = form
        .getFieldValue("dimensions")
        .filter((d: ReportColumn) => {
          return d.name !== dimension.name;
        });
      form.setFieldsValue({ dimensions: filteredDimensions });
    }
  }

  handleDimensionReorder(fromIndex: number, toIndex: number) {
    this.props.reorderDimension(fromIndex, toIndex);
  }

  // Render helpers
  renderMetricModal() {
    const { schema, selected } = this.props.metrics;
    return _.sortBy(schema, "display_order").map((metric: ReportColumn) => {
      return (
        <Checkbox
          className="check-field"
          key={metric.id}
          label={metric.name}
          handleChange={this.handleMetricChange.bind(this, metric)}
          checked={!!_.find(selected, { id: metric.id })}
        />
      );
    });
  }

  renderMetrics() {
    return this.props.metrics.selected.map((metric: ReportColumn) => (
      <Draggable className="column" key={metric.id} id={metric.id}>
        {metric.name}
      </Draggable>
    ));
  }

  renderDimensionModal() {
    const { schema, selected } = this.props.dimensions;
    return _.sortBy(schema, "display_order").map((dimension: ReportColumn) => {
      return (
        <Checkbox
          className="check-field"
          key={dimension.id}
          label={dimension.name}
          handleChange={this.handleDimensionChange.bind(this, dimension)}
          checked={!!_.find(selected, { id: dimension.id })}
        />
      );
    });
  }

  renderDimensions() {
    return this.props.dimensions.selected.map((dimension: ReportColumn) => (
      <Draggable className="column" key={dimension.id} id={dimension.id}>
        {dimension.name}
      </Draggable>
    ));
  }

  // Render
  render() {
    const { getFieldDecorator } = this.props.form;

    return (
      <Section className="report-structure" title="Report Structure">
        <Row>
          <div className="label required">
            Grouped By &nbsp;&nbsp;&nbsp;
            <Popover
              placement="topLeft"
              content={<DimensionPopover />}
              title="Help"
              trigger="hover"
            >
              <Icon type="question-circle" />
            </Popover>
          </div>

          <FormItem help="Please select at least one grouping - Drag to reorder.">
            {getFieldDecorator("dimensions", {
              rules: [{ required: true, type: "array", min: 1 }],
              initialValue: this.props.dimensions.selected
            })(
              <Dropable
                className="container"
                reorder={this.handleDimensionReorder.bind(this)}
                find={this.handleDimensionFind.bind(this)}
              >
                {this.renderDimensions()}
                <div
                  className="add-btn column"
                  onClick={this.toggle.bind(this, "dimensionModal")}
                >
                  +/- Dimension
                </div>
              </Dropable>
            )}
          </FormItem>

          <Modal
            title="Grouped By"
            visible={this.state.dimensionModal}
            closable={false}
            footer={[
              <Button
                key="submit"
                type="primary"
                onClick={this.toggle.bind(this, "dimensionModal")}
              >
                Close
              </Button>
            ]}
          >
            {this.renderDimensionModal()}
          </Modal>
        </Row>

        <Row>
          <div className="label required">
            Metrics &nbsp;&nbsp;&nbsp;
            <Popover
              placement="topLeft"
              content={<MetricPopover />}
              title="Help"
              trigger="hover"
            >
              <Icon type="question-circle" />
            </Popover>
          </div>

          <FormItem help="Please select at least one metric - Drag to reorder.">
            {getFieldDecorator("metrics", {
              rules: [{ required: true, type: "array", min: 1 }],
              initialValue: this.props.metrics.selected
            })(
              <Dropable
                className="container"
                reorder={this.handleMetricReorder.bind(this)}
                find={this.handleMetricFind.bind(this)}
              >
                {this.renderMetrics()}
                <div
                  className="add-btn column"
                  onClick={this.toggle.bind(this, "metricModal")}
                >
                  +/- Metric
                </div>
              </Dropable>
            )}
          </FormItem>

          <Modal
            title="Metrics"
            visible={this.state.metricModal}
            closable={false}
            footer={[
              <Button
                key="submit"
                type="primary"
                onClick={this.toggle.bind(this, "metricModal")}
              >
                Close
              </Button>
            ]}
          >
            {this.renderMetricModal()}
          </Modal>
        </Row>

        <Row>
          <div className="label">
            File Header Preview &nbsp;&nbsp;&nbsp;
            <Popover
              placement="topLeft"
              content={<PreviewPopover />}
              title="Help"
              trigger="hover"
            >
              <Icon type="question-circle" />
            </Popover>
          </div>

          <div className="preview pure-u-g">
            <Table
              className="preview-table pure-u-1"
              columns={this.getPreviewColumns()}
              dataSource={[]}
              bordered
              size="small"
            />
          </div>
        </Row>
      </Section>
    );
  }
}

export default ReportStructure;



// WEBPACK FOOTER //
// ./client/modules/ReportForm/components/StageOne/ReportStructure/component.tsx